/**
 * Progress Sink — configurable output target for agent execution progress.
 *
 * Supports writing to stderr, file, or both (tee). File naming strategies
 * allow single-file append, per-invocation unique files, or daily rotation.
 */

import { openSync, writeSync, closeSync, mkdirSync } from "node:fs";
import { dirname, join, basename, extname } from "node:path";

export interface ProgressSinkOptions {
  /** Write to process.stderr. Default: false */
  stderr?: boolean;
  /** Base file path for log output. */
  file?: string;
  /**
   * File naming strategy:
   * - "single":         always append to `file` (default)
   * - "per-invocation": {dir}/{base}-{YYYY-MM-DDTHHmmss}-{label}{ext}
   * - "daily":          {dir}/{base}-{YYYY-MM-DD}{ext}
   */
  naming?: "single" | "per-invocation" | "daily";
  /** Label for per-invocation naming (e.g. workflow ID). */
  label?: string;
}

export interface ProgressSink {
  write(chunk: string): void;
  close(): void;
  /** Resolved file path (undefined if file output is disabled). */
  readonly filePath?: string;
}

function resolveFilePath(
  base: string,
  naming: "single" | "per-invocation" | "daily",
  label?: string,
): string {
  if (naming === "single") return base;

  const dir = dirname(base);
  const ext = extname(base);
  const stem = basename(base, ext);
  const now = new Date();

  if (naming === "daily") {
    const date = now.toISOString().slice(0, 10);
    return join(dir, `${stem}-${date}${ext}`);
  }

  // per-invocation
  const pad = (n: number) => String(n).padStart(2, "0");
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const suffix = label ? `-${label}` : "";
  return join(dir, `${stem}-${ts}${suffix}${ext}`);
}

export function createProgressSink(options: ProgressSinkOptions): ProgressSink {
  const useStderr = options.stderr ?? false;
  let fd: number | null = null;
  let resolvedPath: string | undefined;

  if (options.file) {
    const naming = options.naming ?? "single";
    resolvedPath = resolveFilePath(options.file, naming, options.label);
    mkdirSync(dirname(resolvedPath), { recursive: true });
    fd = openSync(resolvedPath, "a");
  }

  if (!useStderr && fd === null) {
    return { write() {}, close() {} };
  }

  return {
    write(chunk: string) {
      if (useStderr) process.stderr.write(chunk);
      if (fd !== null) writeSync(fd, chunk);
    },
    close() {
      if (fd !== null) {
        closeSync(fd);
        fd = null;
      }
    },
    get filePath() {
      return resolvedPath;
    },
  };
}
